import { useEffect, useRef, useState } from 'react';
import { businessApi, locationApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';
import { Toggle } from '@/components/inspector/toggle';

// =====================================================================
// PulseStep (WP3 Task 10) — the wizard's Pulse step BODY (spec §8 step 4,
// ONE screen). WizardStep chrome is supplied by the shell (Task 11); this
// component only renders what goes inside it.
//
// AMENDED by CONSOLIDATION §A2, then T1.8: notification prefs are PER-MEMBER
// (`PATCH /api/b/:businessId/member-prefs`) — see reputation-step.tsx for the full
// rationale, which this step mirrors exactly (registerSave fires once, a
// ref keeps the closure reading the latest checkbox value).
//
// Forms-inbox confirm: GET /api/forms/unread-count?siteId= (forms.ts:59 —
// real shape `{ count }`). A successful fetch with count > 0 renders
// "N waiting for you"; count === 0 renders no number (nothing to report,
// not a claim); a FAILED fetch also renders no number — never a fabricated
// zero — but the inbox link itself always renders regardless of fetch
// outcome.
//
// Fix round 1 (consent issue): mirrors reputation-step.tsx — the checkbox
// initializes from the saved pref (`GET /api/b/:businessId/member-prefs`) instead of
// always starting at the D6 default, so a returning owner's earlier opt-out
// isn't silently flipped back to true by Continue. Fetch failure falls back
// to the checked default; while loading the checkbox renders
// disabled+checked (no layout jump either way it resolves).
//
// Readiness gate (WP3 Task 11 review, carried item 2): mirrors
// reputation-step.tsx — `registerSave` now registers inside the prefs GET's
// `.finally()` instead of unconditionally on mount, so a Continue click that
// beats the fetch finds nothing registered and the shell's default no-op
// resolve runs instead (writes nothing, defaults hold). Pinned by
// reputation-pulse.test.tsx's "readiness gate" test.
//
// Consent copy (2026-08-24 adversarial review). The old line promised "each
// one is sent once, when it actually happens", which the delivery rules make
// false in the DEFAULT path — a 48-hour quiet window, a 7-day cap shared with
// the site-live and first-scan emails, a 30-day expiry that drops the row
// unsent — and it named three of the twelve events (omitting the AI citation
// and the website tap, and using "Map Pack", a phrase no milestone email
// uses). The replacement states the real cadence and the real scope; the
// milestones card also carries the same load-failure note the digest card
// has, so a failed prefs GET is never a silently fabricated default.
//
// Task 10 (2026-08-24 pulse-data-milestones-taps): a THIRD toggle —
// `milestones` ("email me when something real happens") — sits beside the
// weekly-digest one. It is a sibling in every respect: same GET init, same
// ref-read Continue-save, same D6 checked default, same disabled-while-
// loading behaviour, and the same card/label/input markup. Both prefs ride
// ONE PATCH on Continue (one save action, one write), so unchecking either
// never resurrects the other's stale value — each field is read from its own
// ref at save time.
// =====================================================================

interface UnreadCountResponse {
  count: number;
}

interface EmailPrefsGetResponse {
  prefs: {
    weeklyDigest: boolean;
    reviewNotifications: boolean;
    milestones: boolean;
  };
}

export function PulseStep({
  siteId,
  registerSave,
}: {
  siteId: string;
  registerSave?: (save: () => Promise<void>) => void;
}) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [milestones, setMilestones] = useState(true);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsFetchFailed, setPrefsFetchFailed] = useState(false);
  const weeklyDigestRef = useRef(weeklyDigest);
  const milestonesRef = useRef(milestones);

  useEffect(() => {
    weeklyDigestRef.current = weeklyDigest;
  }, [weeklyDigest]);

  useEffect(() => {
    milestonesRef.current = milestones;
  }, [milestones]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<EmailPrefsGetResponse>(businessApi(businessId, 'member-prefs'))
      .then((res) => {
        if (cancelled) return;
        setWeeklyDigest(res.prefs.weeklyDigest);
        setMilestones(res.prefs.milestones);
      })
      .catch(() => {
        // Fetch failure: leave both toggles at the D6 checked default and
        // let the owner proceed — the save PATCH on Continue is still an
        // explicit user action, never a silent write. Flag the failure so we
        // can render an honest note.
        setPrefsFetchFailed(true);
      })
      .finally(() => {
        if (cancelled) return;
        setPrefsLoading(false);
        // Readiness gate (WP3 Task 11 review, carried item 2): mirrors
        // reputation-step.tsx — register the Continue-save only once the
        // saved-prefs fetch settles, so a Continue click that beats the GET
        // finds nothing registered and writes nothing (defaults hold).
        registerSave?.(async () => {
          await api.patch(businessApi(businessId, 'member-prefs'), {
            weeklyDigest: weeklyDigestRef.current,
            milestones: milestonesRef.current,
          });
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const [unreadCount, setUnreadCount] = useState<number | null>(null);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    api
      .get<UnreadCountResponse>(locationApi(businessId, siteId, 'forms/unread-count'))
      .then((res) => {
        if (!cancelled) setUnreadCount(res.count);
      })
      .catch(() => {
        // Fetch failure: leave unreadCount at null so the link renders
        // without a number — never a fabricated zero.
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border-subtle bg-card p-6">
        <p className="text-sm text-text-secondary">
          messages from your website's contact form land in your inbox
        </p>
        {unreadCount !== null && unreadCount > 0 ? (
          <p className="mt-2 text-sm font-semibold text-foreground">
            {unreadCount} waiting for you
          </p>
        ) : null}
        <Link
          to={locationRoutes.inbox(businessId, siteId)}
          className="mt-3 inline-block text-sm font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
        >
          See your inbox →
        </Link>
      </div>

      <div className="rounded-xl border border-border-subtle bg-card p-6">
        {/* T12/R2: a notification preference is a switch, not a checkbox. */}
        <Toggle
          label="Send me a weekly email with what changed"
          checked={weeklyDigest}
          disabled={prefsLoading}
          onChange={setWeeklyDigest}
        />
        {prefsFetchFailed && !prefsLoading && (
          <p className="mt-2 text-sm text-text-secondary">
            Couldn't load your saved preference — showing the default
          </p>
        )}
        <p className="mt-2 text-sm text-text-secondary">
          Your weekly email starts after your first Monday check.
        </p>
      </div>

      <div className="rounded-xl border border-border-subtle bg-card p-6">
        <Toggle
          label="Email me when something real happens on your profile or map"
          checked={milestones}
          disabled={prefsLoading}
          onChange={setMilestones}
        />
        {prefsFetchFailed && !prefsLoading && (
          <p className="mt-2 text-sm text-text-secondary">
            Couldn't load your saved preference — showing the default
          </p>
        )}
        <p className="mt-2 text-sm text-text-secondary">
          At most one a week, after your first 48 hours: your first call, direction or website tap
          from Google, first appearances in the map&rsquo;s top three, review-count steps, and the
          first time an AI engine cites you.
        </p>
      </div>
    </div>
  );
}
